import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import {
  usePopoverState,
  Popover as BasePopover,
  PopoverDisclosure,
} from 'reakit/Popover';
import { hook } from '@g4/automation-hooks';

import { getRefPropType } from '../../../utils/propTypesUtils';
import { ConditionalWrap } from '../../Boneyard';
import { Portal } from '../../Boneyard';

export const propTypes = {
  backgroundColor: PropTypes.string.isRequired,
  arrowSize: PropTypes.number,
  arrowStyles: PropTypes.object,
  children: PropTypes.node.isRequired,
  content: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
  showClose: PropTypes.bool,
  iconClose: PropTypes.element,
  automationHook: PropTypes.string,
  arrowRef: getRefPropType(),
  arrowOffset: PropTypes.string,
  placement: PropTypes.string,
  size: PropTypes.string,
  triggerWrapper: PropTypes.func,
  horizontalOffset: PropTypes.number,
  onClick: PropTypes.func,
};

export const defaultProps = {
  arrowSize: 20,
  maxWidth: '100%',
  showClose: true,
  iconClose: <IconClose />,
  placement: 'bottom',
  horizontalOffset: 0,
  onClick: Function.prototype,
};

function IconClose() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
    >
      <g fill="currentColor" fillRule="evenodd">
        <rect
          width="2"
          height="24"
          x="11.192"
          y=".192"
          rx="1"
          transform="rotate(45 12.192 12.192)"
        />
        <path d="M20.678 20.678a1 1 0 0 1-1.415 0L3.707 5.12a1 1 0 1 1 1.414-1.414l15.557 15.556a1 1 0 0 1 0 1.415z" />
      </g>
    </svg>
  );
}

const PopoverContainer = styled.div`
  position: relative;
  background: ${props => props.backgroundColor};
  color: ${props => props.theme.colors.popover.text};
  border-radius: 8px;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  ${({ width }) => width && `width: ${width};`}
  max-width: ${props => props.maxWidth};
  margin-left: ${props => props.theme.spacing(props.horizontalOffset)};
`;

const PopoverArrow = withTheme(({ theme, ...props }) => {
  const {
    unstable_arrowStyles: arrowStyles,
    unstable_arrowRef: arrowRef,
    size,
    backgroundColor,
    arrowOffset,
  } = props;

  const [placement] = props.placement.split('-');

  const transformMap = {
    top: 'rotateZ(180deg)',
    right: 'rotateZ(-90deg)',
    bottom: 'rotateZ(360deg)',
    end: 'rotateZ(360deg)',
    left: 'rotateZ(90deg)',
  };

  const style = {
    ...arrowStyles,
    position: 'absolute',
    zIndex: 998,
    width: size,
    height: size,
    pointerEvents: 'none',
    transform: transformMap[placement],
    [placement]: '100%',
    marginLeft: arrowOffset && arrowOffset > 0 ? arrowOffset / 2 : 0,
  };

  return (
    <div style={style} ref={arrowRef} className={props.className}>
      <svg viewBox="0 0 30 30" style={{ display: 'block' }}>
        <path
          fill={backgroundColor}
          strokeWidth="0"
          d="m0,30l15,-15l15,15l-30,0z"
        />
      </svg>
    </div>
  );
});

const CloseIcon = styled.button.attrs({
  type: 'button',
  'aria-label': 'Close',
})`
  position: absolute;
  top: 16px;
  right: 20px;
  color: currentColor;

  ${props => props.theme.query.lg} {
    top: ${props => props.closeIconPosition ?? '32px'};
    right: 32px;
  }
`;

const Popover = ({
  automationHook,
  children,
  content,
  label,
  arrowSize,
  backgroundColor,
  width,
  maxWidth,
  iconClose,
  showClose,
  arrowOffset,
  placement,
  triggerWrapper,
  horizontalOffset,
  onClick,
  closeIconPosition,
}) => {
  const popover = usePopoverState({
    gutter: arrowSize / 2,
    placement,
  });

  const previous = React.useRef();
  const next = React.useRef();

  const FocusTrap = ({ refToFocus }) => {
    return (
      <div
        tabIndex={0}
        aria-hidden="true"
        onFocus={() => refToFocus.current.focus()}
      />
    );
  };

  FocusTrap.propTypes = {
    refToFocus: PropTypes.shape({
      current: PropTypes.shape({
        focus: PropTypes.func,
      }),
    }),
  };

  return (
    <div
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
    >
      <ConditionalWrap condition={triggerWrapper} wrap={triggerWrapper}>
        <PopoverDisclosure
          {...popover}
          ref={previous}
          {...hook(automationHook, 'trigger')}
        >
          {children}
        </PopoverDisclosure>
      </ConditionalWrap>
      <Portal>
        <FocusTrap refToFocus={previous} />
        <BasePopover
          {...popover}
          aria-label={label}
          style={{ zIndex: 998 }}
          tabIndex={0}
        >
          <PopoverArrow
            size={arrowSize}
            backgroundColor={backgroundColor}
            arrowOffset={arrowOffset}
            {...popover}
          />
          <PopoverContainer
            backgroundColor={backgroundColor}
            maxWidth={maxWidth}
            width={width}
            horizontalOffset={horizontalOffset}
            {...hook(automationHook, 'content')}
            /*
            Attach a className to popover in order to reference it for purposes of
            useOnClickOutside edge case handling (see e.g. SiteHeader render method)
          */
            className="g4-popover-content"
          >
            {showClose && (
              <CloseIcon
                onClick={popover.hide}
                {...hook(automationHook, 'close')}
                closeIconPosition={closeIconPosition}
              >
                {iconClose}
              </CloseIcon>
            )}
            {content}
          </PopoverContainer>
        </BasePopover>
        <FocusTrap refToFocus={next} />
      </Portal>
    </div>
  );
};

Popover.propTypes = propTypes;
Popover.defaultProps = defaultProps;

export default Popover;
